import React, { useState, createContext } from "react";
import _ from "lodash";
import apiRequest from "../../utils/apiRequest";
import { hideCanvasSelectLabel } from "../../utils/canvasSelectLabel";
export const VariantContextData = createContext();

let layerTreeCache = null;

const REPARENT_SETTINGS = [
  "[step_id]",
  "[section_id]",
  "[section_column_id]",
  "[element_id]",
  "[content_toggle_item_id]",
];

export const VariantBuilderContext = (props) => {
  // define the builder's context object
  const queryParameters = new URLSearchParams(window.location.search);

  let context = {
    selected_object_type: null,
    selected_object_id: null,
    selected_object_path: null,
    selected_object_tab: null,
    selected_object_collapse: null,
    selected_object_editing: false,
    objects: {},
    history: {
      changes: [],
      undos: 0,
      unsaved: null,
      unpublished: null,
    },
    device:
      queryParameters.get("device") ||
      getCookie("cf-builder-current-device") ||
      "desktop",
    panel_closed: false,
    dark_mode: getCookie("darkMode") ? getCookie("darkMode") == "true" : false,
    zoomable_canvas_locked: getCookie("zoomableCanvasLocked")
      ? getCookie("zoomableCanvasLocked") == "true"
      : true,
    view: "funnel",
    new_object: {},
    remix_mappings: props.remix_mappings,
    environment: window.environment,
  };

  if (props.variant.options["unpublished"] == "true") {
    context.history.unpublished = true;
  }

  context.logo = context.dark_mode ? props.logo_white : props.logo;
  context.objects["cta"] = props.cta;
  context.objects["variant"] = props.variant;
  context.objects["website"] = props.website;
  context.objects["users"] = props.users;
  context.objects["current_user"] = props.current_user;
  context.objects["subscription"] = props.subscription;
  context.objects["account"] = props.account;

  const LAYER_OBJECT_TYPES = [
    "sections",
    "section_columns",
    "elements",
    "fields",
  ];

  const clearCachedLayerTree = () => {
    layerTreeCache = null;
  };

  const clearSelectedObject = (builder) => {
    builder["selected_object_type"] = null;
    builder["selected_object_id"] = null;
    builder["selected_object_path"] = null;
    builder["selected_object_tab"] = null;
    builder["selected_object_collapse"] = null;
    builder["selected_object_editing"] = false;
  };

  const objectBelongsToRemovedObject = (
    selected_type,
    selected_id,
    removed_type,
    removed_id,
    variant
  ) => {
    if (!selected_type || !selected_id) return false;
    if (selected_type == removed_type && selected_id == removed_id) return true;

    const selectedObject = _.get(
      variant,
      `${selected_type}.${selected_id}`,
      undefined
    );
    if (!selectedObject) return true;
    if (selectedObject.toBeDeleted) return true;

    if (removed_type == "elements") {
      if (selectedObject.element_id == removed_id) return true;

      if (selected_type == "fields") {
        const fieldElement = _.get(
          variant,
          `elements.${selectedObject.element_id}`,
          undefined
        );
        return !fieldElement || fieldElement.toBeDeleted == true;
      }

      if (selected_type == "sections") {
        return selectedObject.element_id == removed_id;
      }
    }

    if (removed_type == "sections") {
      if (selected_type == "fields") {
        const fieldElement = _.get(
          variant,
          `elements.${selectedObject.element_id}`,
          undefined
        );
        return !!fieldElement && fieldElement.section_id == removed_id;
      }
      return selectedObject.section_id == removed_id;
    }

    return false;
  };

  const checkProductVariant = (product_variant, context) => {
    context.objects.variant.product_variants[product_variant.id] =
      product_variant;

    return context;
  };

  const checkSectionColumn = (section_column, context) => {
    context.objects.variant.section_columns[section_column.id] = section_column;

    return context;
  };

  const checkEmbedRecommendation = (embed_recommendation, context) => {
    context.objects.variant.embed_recommendations =
      context.objects.variant.embed_recommendations || {};
    context.objects.variant.embed_recommendations[embed_recommendation.id] = embed_recommendation;

    Object.values(embed_recommendation?.conditions || {}).forEach((condition) => {
      checkCondition(condition, context);
    });

    return context;
  };

  const checkSection = (section, context) => {
    context.objects.variant.sections[section.id] = section;

    Object.values(section.elements || {}).forEach((element) => {
      checkElement(element, context);
    });

    Object.values(section.section_columns || {}).forEach((section_column) => {
      checkSectionColumn(section_column, context);
    });

    return context;
  };

  const checkElement = (element, context) => {
    context.objects.variant.elements[element.id] = element;

    Object.values(element?.action_groups || {}).forEach((action_group) => {
      checkActionGroup(action_group, context);
    });

    Object.values(element?.actions || {}).forEach((action) => {
      checkAction(action, context);
    });

    Object.values(element?.content_list_items || {}).forEach(
      (content_list_item) => {
        checkContentListItem(content_list_item, context);
      }
    );

    Object.values(element?.content_toggle_items || {}).forEach(
      (content_toggle_item) => {
        checkContentToggleItem(content_toggle_item, context);
      }
    );

    Object.values(element?.fields || {}).forEach((field) => {
      checkField(field, context);
    });

    Object.values(element?.product_recommendations || {}).forEach(
      (product_recommendation) => {
        checkProductRecommendation(product_recommendation, context);
      }
    );

    Object.values(element?.review_recommendations || {}).forEach((review_recommendation) => {
      checkReviewRecommendation(review_recommendation, context);
    });

    Object.values(element?.scheduler_recommendations || {}).forEach((scheduler_recommendation) => {
      checkSchedulerRecommendation(scheduler_recommendation, context);
    });

    Object.values(element?.embed_recommendations || {}).forEach((embed_recommendation) => {
      checkEmbedRecommendation(embed_recommendation, context);
    });

    Object.values(element?.products || {}).forEach((product) => {
      checkProduct(product, context);
    });

    Object.values(element?.sections || {}).forEach((section) => {
      checkSection(section, context);
    });

    return context;
  };

  const checkField = (field, context) => {
    context.objects.variant.fields[field.id] = field;

    Object.values(field?.field_options || {}).forEach((field_option) => {
      checkFieldOption(field_option, context);
    });

    Object.values(field?.fields || {}).forEach((nestedField) => {
      checkField(nestedField, context);
    });

    Object.values(field?.action_groups || {}).forEach((action_group) => {
      checkActionGroup(action_group, context);
    });

    return context;
  };

  const checkFieldOption = (field_option, context) => {
    context.objects.variant.field_options[field_option.id] = field_option;

    return context;
  };

  const checkActionGroup = (action_group, context) => {
    context.objects.variant.action_groups[action_group.id] = action_group;

    Object.values(action_group?.actions || {}).forEach((action) => {
      checkAction(action, context);
    });

    Object.values(action_group?.conditions || {}).forEach((condition) => {
      checkCondition(condition, context);
    });

    return context;
  };

  const checkCondition = (condition, context) => {
    context.objects.variant.conditions[condition.id] = condition;

    return context;
  };

  const checkProduct = (product, context) => {
    context.objects.variant.products[product.id] = product;

    Object.values(product?.product_variants || {}).forEach(
      (product_variant) => {
        checkProductVariant(product_variant, context);
      }
    );

    return context;
  };

  const checkProductRecommendation = (product_recommendation, context) => {
    context.objects.variant.product_recommendations[product_recommendation.id] =
      product_recommendation;

    Object.values(product_recommendation?.products || {}).forEach((product) => {
      checkProduct(product, context);
    });

    Object.values(product_recommendation?.conditions || {}).forEach(
      (condition) => {
        checkCondition(condition, context);
      }
    );

    return context;
  };

  const checkReviewRecommendation = (review_recommendation, context) => {
    context.objects.variant.review_recommendations[review_recommendation.id] = review_recommendation;

    Object.values(review_recommendation?.conditions || {}).forEach((condition) => {
      checkCondition(condition, context);
    });

    return context;
  };

  const checkSchedulerRecommendation = (scheduler_recommendation, context) => {
    context.objects.variant.scheduler_recommendations =
      context.objects.variant.scheduler_recommendations || {};
    context.objects.variant.scheduler_recommendations[scheduler_recommendation.id] = scheduler_recommendation;

    Object.values(scheduler_recommendation?.conditions || {}).forEach((condition) => {
      checkCondition(condition, context);
    });

    return context;
  };

  const checkAction = (action, context) => {
    context.objects.variant.actions[action.id] = action;

    return context;
  };

  const checkContentListItem = (content_list_item, context) => {
    context.objects.variant.content_list_items[content_list_item.id] =
      content_list_item;

    return context;
  };

  const checkContentToggleItem = (content_toggle_item, context) => {
    context.objects.variant.content_toggle_items[content_toggle_item.id] =
      content_toggle_item;

    Object.values(content_toggle_item?.conditions || {}).forEach((condition) => {
      checkCondition(condition, context);
    });

    return context;
  };

  const checkStep = (step, context) => {
    context.objects.variant.steps[step.id] = step;

    Object.values(step?.sections || {}).forEach((section) => {
      checkSection(section, context);
    });

    Object.values(step?.action_groups || {}).forEach((action_group) => {
      checkActionGroup(action_group, context);
    });

    return context;
  };

  // set context object as state
  const [contextData, setContextData] = useState(context);

  let builder = {
    mobile: function () {
      let updatedBuilder = { ...contextData };
      updatedBuilder.device = "mobile";
      setContextData((originalData) => updatedBuilder);
      setCookie("cf-builder-current-device", "mobile");
    },

    desktop: function () {
      let updatedBuilder = { ...contextData };
      updatedBuilder.device = "desktop";
      setContextData((originalData) => updatedBuilder);
      setCookie("cf-builder-current-device", "desktop");
    },

    panel: function (closed) {
      let updatedBuilder = { ...contextData };
      updatedBuilder.panel_closed = closed;
      setContextData((originalData) => updatedBuilder);

      window.panel_closed = closed;
    },

    // builder.select populates panel settings based on the selected object
    select: function (
      object_type,
      object_id,
      tab,
      collapse,
      editing = false,
      close_panel
    ) {
      let updatedBuilder = { ...contextData };

      // Validate object exists before selecting it
      if (object_type !== "variants") {
        const objectPath = `objects.variant.${object_type}.${object_id}`;
        const object = _.get(contextData, objectPath);

        if (!object || object.toBeDeleted) {
          console.warn(
            `Object not found or marked for deletion: ${object_type}/${object_id}`
          );
          return;
        }
      }

      updatedBuilder["selected_layer_object_type"] = object_type;
      updatedBuilder["selected_layer_object_id"] = object_id;
      updatedBuilder["selected_object_type"] = object_type;
      updatedBuilder["selected_object_id"] = object_id;
      updatedBuilder["selected_object_editing"] = editing ? editing : false;
      updatedBuilder["view"] =
        object_type == "variants"
          ? "settings"
          : object_type == "steps"
          ? "funnel"
          : "layers";

      updatedBuilder["selected_object_path"] =
        object_type !== "variants"
          ? `[objects][variant][${object_type}][${object_id}]`
          : "[objects][variant]";

      updatedBuilder["selected_object_tab"] = tab ? tab : null;
      updatedBuilder["selected_object_collapse"] = collapse ? collapse : null;

      if (close_panel !== null && close_panel !== undefined) {
        updatedBuilder["panel_closed"] = close_panel;
        window.panel_closed = close_panel;
      }

      updatedBuilder.selected_item_parent_id = null;
      updatedBuilder.selected_item_id = null;
      updatedBuilder.selected_item_type = null;
      setContextData((originalData) => updatedBuilder);

      window.selected_object_type = object_type;
      window.selected_object_id = object_id;
      window.selected_object_tab = updatedBuilder.selected_object_tab;
      window.selected_object_collapse = updatedBuilder.selected_object_collapse;
      window.selected_object_editing = updatedBuilder.selected_object_editing;

      document
        .querySelectorAll(
          `.editor-select, .select-label:not([data-object_id="${object_id}"]), .select-label-popover:not([data-object_id="${object_id}"])`
        )
        .forEach((selected) => {
          if ($(selected).is(":visible") == false) return;

          if (selected.classList.contains("editor-select")) {
            if (
              selected.getAttribute("data-object_id") !== object_id + "" &&
              selected.getAttribute("data-object-id") !== object_id + ""
            ) {
              selected.classList.remove("editor-select");
            }
          }

          if (selected.classList.contains("select-label")) {
            hideCanvasSelectLabel();
          }

          if (selected.classList.contains("select-label-popover")) {
            selected.style.display = "none";
          }
        });

      onboard.checklist.close();
    },

    get_object_parent_layer: function (object_type, object_id) {
      let object = _.get(
        contextData.objects.variant,
        `${object_type}.${object_id}`,
        undefined
      );
      let parent_object = null;
      let parent_object_type = null;
      let section = contextData.objects.variant.sections[object_id];

      if (object && object_type == "elements") {
        parent_object = contextData.objects.variant.sections[object.section_id];
        parent_object_type = "sections";
      } else if (object && object_type == "section_columns") {
        parent_object = contextData.objects.variant.sections[object.section_id];
        parent_object_type = "sections";
      } else if (object_type == "sections" && section) {
        if (section.content_toggle_item_id) {
          parent_object =
            contextData.objects.variant.content_toggle_items[
              section.content_toggle_item_id
            ];
          parent_object_type = "content_toggle_items";
        } else if (section.element_id) {
          parent_object =
            contextData.objects.variant.elements[section.element_id];
          parent_object_type = "elements";
        } else {
          parent_object = contextData.objects.variant.steps[section.step_id];
          parent_object_type = "steps";
        }
      } else if (object && object_type == "content_toggle_items") {
        parent_object = contextData.objects.variant.elements[object.element_id];
        parent_object_type = "elements";
      }

      return {
        object: parent_object,
        object_type: parent_object_type,
      };
    },

    selected_object_layer: function (object_type, object_id) {
      if (!object_type || !object_id) return [];

      const object =
        object_type == "variants"
          ? contextData.objects.variant
          : _.get(
              contextData.objects.variant,
              `${object_type}.${object_id}`,
              undefined
            );

      if (!object || object.toBeDeleted) return [];

      const layerTreeCacheKey = `${object_type}-${object_id}`;

      if (layerTreeCache && layerTreeCache.cache_key == layerTreeCacheKey) {
        return layerTreeCache.object;
      } else {
        let current_object_loop_type = object_type;
        let current_object_loop_id = object_id;

        let element_index = 0;

        const layerTree = new LayerTreeService(element_index, object);

        while (true) {
          let parent_layer = builder.get_object_parent_layer(
            current_object_loop_type,
            current_object_loop_id
          );

          if (!parent_layer.object) {
            break;
          }

          layerTree.insert(
            element_index,
            element_index + 1,
            parent_layer.object
          );

          element_index++;
          current_object_loop_type = parent_layer.object_type;
          current_object_loop_id = parent_layer.object.id;
        }

        const orderedTree = [...layerTree.inOrderTraversal()].map(x => x.value);

        const slimTree = orderedTree.map((node) => ({
          id: node.id,
          column: node.column,
        }));
        layerTreeCache = { cache_key: layerTreeCacheKey, object: slimTree };
        // layerTreeCache is per-session only (avoid persisting to localStorage)

        return slimTree;
      }
    },

    selected: function () {
      return {
        object: _.get(contextData, contextData.selected_object_path, undefined),
        object_type: contextData.selected_object_type,
        object_id: contextData.selected_object_id,
        object_collapse: contextData.selected_object_collapse,
        object_tab: contextData.selected_object_tab,
      };
    },

    // called when a setting is adjusted
    update: function (builderUpdates) {
      let updatedBuilder = { ...contextData };
      let historyUpdates = [];

      // for each change, update the builder state and add change to history
      builderUpdates.forEach((change) => {
        let object_path = "[objects][variant]";

        if (change.object_type !== "variants") {
          object_path += "[" + change.object_type + "]";

          if (change.object_id) {
            object_path += "[" + change.object_id + "]";
          }
        }

        let object = _.get(updatedBuilder, object_path);
        const setting_path = object_path + change.setting_name;
        const old_value = change.old_value
          ? change.old_value
          : _.get(updatedBuilder, setting_path);

        updatedBuilder = _.set(updatedBuilder, setting_path, change.value);
        updatedBuilder = _.set(
          updatedBuilder,
          object_path + "[toBeUpdated]",
          true
        );
        updatedBuilder = _.set(
          updatedBuilder,
          object_path + "[lastUpdated]",
          Date.now()
        );

        updatedBuilder["selected_object_editing"] = false;

        if (REPARENT_SETTINGS.includes(change.setting_name)) {
          clearCachedLayerTree();
        }

        if (change.skip_history !== true) {
          historyUpdates.push({
            object_type: change.object_type,
            object_id: change.object_id,
            setting_name: change.setting_name,
            value: change.value,
            old_value: old_value,
          });
        }
      });

      if (historyUpdates.length > 0) {
        updatedBuilder = addChangeToHistory(updatedBuilder, {
          updates: historyUpdates,
          timestamp: Date.now(),
        });
      }

      setContextData((originalData) => updatedBuilder);
      builder.clearUnsavedLoop();
      builder.setUnsavedLoop();
    },

    // makes API POST and stores object in context
    create: function (object_type, payload, options) {
      const api_endpoint = "./" + object_type + "/";

      return apiRequest("POST", api_endpoint, payload).then((result) => {
        const data = result && result.json;
        // Require a persisted id — responses like { errors: ... } used to be
        // treated as success (they lack `error`), which left ghost objects in
        // the builder that vanished on refresh.
        if (
          !result ||
          result.code < 200 ||
          result.code >= 300 ||
          !data ||
          data.id === undefined ||
          data.id === null ||
          data.error ||
          data.errors
        ) {
          const message =
            (data && (data.error || data.errors)) ||
            (result && result.message) ||
            `create ${object_type} failed`;
          return Promise.reject(message);
        }

        const skip_history = options && options.skip_history;
        let created = data;

        if (options && options.transform) {
          created = options.transform(created);
        }

        builder.addObjectToBuilder(
          object_type,
          created,
          skip_history,
          options ? options.updates : undefined,
          options && options.forceRender ? true : false
        );

        if (options && options.callback) {
          options.callback(created);
        }

        if (
          skip_history !== true &&
          ["steps", "sections", "elements"].includes(object_type) &&
          (!options || options.skip_select !== true)
        ) {
          builder.select(object_type, created.id);
        }

        return created;
      });
    },

    // marks the object to be deleted, adds removal to history
    remove: function (object_type, object_id, message, updates) {
      if (!message || confirm(message)) {
        let updatedBuilder = { ...contextData };
        let object = updatedBuilder.objects.variant[object_type][object_id];
        updates = updates ? updates : [];

        if (object) {
          if (`${object_id}`.startsWith("temp-")) {
            window.cancelledDuplicateReplicas =
              window.cancelledDuplicateReplicas || {};
            window.cancelledDuplicateReplicas[
              `${object_type}-${`${object_id}`.replace("temp-", "")}`
            ] = true;
          }

          object.toBeDeleted = true;
          updates.push({
            object_type: object_type,
            object_id: object_id,
            setting_name: "[toBeDeleted]",
            value: true,
            old_value: undefined,
          });

          updatedBuilder = addChangeToHistory(updatedBuilder, {
            updates: updates,
            timestamp: Date.now(),
          });

          const selectedObjectType = updatedBuilder["selected_object_type"];
          const selectedObjectId = updatedBuilder["selected_object_id"];
          const selectedObjectWasRemoved = objectBelongsToRemovedObject(
            selectedObjectType,
            selectedObjectId,
            object_type,
            object_id,
            updatedBuilder.objects.variant
          );

          if (selectedObjectWasRemoved) {
            clearSelectedObject(updatedBuilder);
            updatedBuilder["view"] =
              object_type == "steps" ? "funnel" : "layers";
            updatedBuilder["selected_layer_object_type"] = null;
            updatedBuilder["selected_layer_object_id"] = null;
            clearCachedLayerTree();
          }

          if (
            updatedBuilder["selected_layer_object_type"] == object_type &&
            updatedBuilder["selected_layer_object_id"] == object_id
          ) {
            updatedBuilder["selected_layer_object_type"] = null;
            updatedBuilder["selected_layer_object_id"] = null;
            clearCachedLayerTree();
          }

          if (
            ![
              "actions",
              "conditions",
              "fields",
              "action_groups",
              "section_columns",
            ].includes(object_type)
          ) {
            updatedBuilder["view"] =
              object_type == "steps" ? "funnel" : "layers";
          }

          if (
            updatedBuilder["view"] == "layers" &&
            !LAYER_OBJECT_TYPES.includes(updatedBuilder["selected_object_type"])
          ) {
            clearSelectedObject(updatedBuilder);
          }
          updatedBuilder["panel_closed"] = false;

          setContextData((originalData) => updatedBuilder);
          if (selectedObjectWasRemoved) {
            window.selected_object_type = null;
            window.selected_object_id = null;
            window.selected_object_tab = null;
            window.selected_object_collapse = null;
            window.selected_object_editing = false;
          }
          builder.setUnsavedLoop();
        }
      }
    },

    // undo last update, increment undo index
    undo: function () {
      let updatedBuilder = { ...contextData };
      let undosIndex = updatedBuilder.history.undos;
      let change = updatedBuilder.history.changes[undosIndex];
      let fallbackToLayers = false;

      if (change) {
        change.updates.forEach((update, i) => {
          let object_path = "[objects][variant]";

          if (update.object_type !== "variants") {
            object_path += "[" + update.object_type + "]";
          }
          if (update.object_id) {
            object_path += "[" + update.object_id + "]";
          }

          updatedBuilder = _.set(
            updatedBuilder,
            object_path + update.setting_name,
            update.old_value
          );

          updatedBuilder = _.set(
            updatedBuilder,
            object_path + "[lastUpdated]",
            Date.now()
          );

          if (!fallbackToLayers) {
            fallbackToLayers =
              update.setting_name == "[toBeDeleted]" &&
              ["steps", "sections", "elements"].includes(update.object_type);
          }
        });

        updatedBuilder.history.undos = updatedBuilder.history.undos + 1;
        updatedBuilder.history.unsaved = true;
        if (fallbackToLayers) updatedBuilder.view = "layers";

        setContextData((originalData) => updatedBuilder);
        builder.setUnsavedLoop();
      }
    },

    // redo last update, decrement undo index
    redo: function () {
      let updatedBuilder = { ...contextData };
      let undosIndex = updatedBuilder.history.undos - 1;
      let change = updatedBuilder.history.changes[undosIndex];
      let fallbackToLayers = false;

      if (change) {
        change.updates.forEach((update, i) => {
          let object_path = "[objects][variant]";

          if (update.object_type !== "variants") {
            object_path += "[" + update.object_type + "]";
          }
          if (update.object_id) {
            object_path += "[" + update.object_id + "]";
          }

          updatedBuilder = _.set(
            updatedBuilder,
            object_path + update.setting_name,
            update.value
          );

          updatedBuilder = _.set(
            updatedBuilder,
            object_path + "[lastUpdated]",
            Date.now()
          );

          if (!fallbackToLayers) {
            fallbackToLayers =
              update.setting_name == "[toBeDeleted]" &&
              ["steps", "sections", "elements"].includes(update.object_type);
          }
        });

        updatedBuilder.history.undos = updatedBuilder.history.undos - 1;
        updatedBuilder.history.unsaved = true;
        if (fallbackToLayers) updatedBuilder.view = "layers";

        setContextData((originalData) => updatedBuilder);
        builder.setUnsavedLoop();
      }
    },

    new_object: (payload) => {
      const noStepsAvailable =
        Object.values(contextData.objects.variant.steps).filter(
          (step) => !step.toBeDeleted
        ).length == 0;
      const noSectionsAvailable =
        Object.values(contextData.objects.variant.sections).filter(
          (section) =>
            !section.element_id &&
            !section.content_toggle_item_id &&
            !section.toBeDeleted
        ).length == 0;

      let updatedBuilder = { ...contextData };
      updatedBuilder["view"] = "create";

      if (
        ["Sections", "Elements"].includes(payload.object_type) &&
        noStepsAvailable
      ) {
        updatedBuilder["new_object"] = {
          object_type: "Steps",
        };
      } else if (payload.object_type == "Elements" && noSectionsAvailable) {
        updatedBuilder["new_object"] = {
          object_type: "Sections",
        };
      } else {
        updatedBuilder["new_object"] = {
          ...updatedBuilder["new_object"],
          ...payload,
          // capture the selection (cleared below) so the create panel can
          // still target it (e.g. the Remix AI creation prompt breadcrumb)
          selection_object_type: contextData.selected_object_type,
          selection_object_id: contextData.selected_object_id,
        };
      }

      updatedBuilder["selected_object_type"] = null;
      updatedBuilder["selected_object_id"] = null;
      updatedBuilder["selected_object_collapse"] = null;
      updatedBuilder["selected_object_tab"] = null;
      setContextData((originalData) => updatedBuilder);

      window.selected_object_type = null;
      window.selected_object_id = null;
      window.selected_object_tab = null;
      window.selected_object_collapse = null;
      window.selected_object_editing = null;
    },

    addBuilderObjectFromApiToBuilder: (
      object_type,
      object,
      skip_history,
      updates,
      forceRender
    ) => {
      let updatedBuilder = { ...contextData };
      updates = updates ? updates : [];
      object["toBeCreated"] = true;
      object["isLoading"] = false;

      switch (object_type) {
        case "steps":
          checkStep(object, updatedBuilder);
          break;
        case "sections":
          checkSection(object, updatedBuilder);
          break;
        case "elements":
          checkElement(object, updatedBuilder);
          break;
        case "section_columns":
          checkSectionColumn(object, updatedBuilder);
          Object.values(object.elements || {}).forEach((element) => {
            checkElement(element, updatedBuilder);
          });
          break;
      }

      if (skip_history !== true) {
        updates.push({
          object_type: object_type,
          object_id: object.id,
          setting_name: "[toBeDeleted]",
          value: undefined,
          old_value: true,
        });

        if (forceRender == true) {
          setContextData((originalData) => updatedBuilder);
          dispatchCustomEvent("updateBuilder", { updates: updates });
        } else {
          updatedBuilder = addChangeToHistory(updatedBuilder, {
            updates: updates,
            timestamp: Date.now(),
          });
        }
      }

      if (forceRender !== true) {
        setContextData((originalData) => updatedBuilder);
        builder.setUnsavedLoop();
      }
    },

    builderChangesToAsyncDuplicate: () => {
      let updatedBuilder = { ...contextData };

      let flat_history = updatedBuilder.history.changes.flatMap(
        (change) => change.updates
      );
      let history_changes = Object.values(flat_history).filter((change) => {
        const builder_object =
          updatedBuilder?.objects?.variant?.[change.object_type]?.[
            change.object_id
          ];

        if (change["setting_name"] == "[toBeDeleted]") {
          return builder_object?.toBeDeleted == true;
        }

        if (change["setting_name"] == "[toBeUpdated]") {
          return builder_object?.toBeUpdated == true;
        }

        if (change["setting_name"] == "[toBeCreated]") {
          return builder_object?.toBeCreated == true;
        }

        return true;
      });

      return history_changes;
    },

    manageReplicaObject: (object_type, object_id, operation) => {
      let updatedBuilder = { ...contextData };
      const isAdd = operation === "add";
      const originalObject =
        contextData.objects.variant[object_type][object_id];

      const tempId = (id) => `temp-${id || _.uniqueId()}`;

      const processObject = (obj, objType, updates) => {
        const replicaId = tempId(obj.id);
        if (isAdd) {
          updatedBuilder.objects.variant[objType][replicaId] = {
            ..._.cloneDeep(obj),
            id: replicaId,
            ...updates,
          };
        } else {
          delete updatedBuilder.objects.variant[objType][replicaId];
        }

        return replicaId;
      };

      const processContentToggleItem = (
        content_toggle_item,
        parent_updates
      ) => {
        const new_content_toggle_item_id = tempId(content_toggle_item.id);

        processObject(content_toggle_item, "content_toggle_items", {
          ...parent_updates,
          loaded: false,
        });

        Object.values(contextData.objects.variant.sections)
          .filter(
            (s) =>
              s.content_toggle_item_id === content_toggle_item.id &&
              !s.toBeDeleted
          )
          .forEach((s) =>
            processSection(s, {
              content_toggle_item_id: new_content_toggle_item_id,
            })
          );
      };

      const processElement = (element, parent_updates, options = {}) => {
        const new_element_id = tempId(element.id);
        const position =
          options.keepPosition == true
            ? element.position
            : element.position + 1;

        processObject(element, "elements", {
          ...parent_updates,
          position: position,
          loaded: false,
        });

        Object.values(contextData.objects.variant.content_toggle_items)
          .filter(
            (toggle_item) =>
              toggle_item.element_id === element.id && !toggle_item.toBeDeleted
          )
          .forEach((toggle_item) =>
            processContentToggleItem(toggle_item, {
              element_id: new_element_id,
            })
          );

        Object.values(contextData.objects.variant.sections)
          .filter(
            (section) =>
              section.element_id === element.id && !section.toBeDeleted
          )
          .forEach((section) =>
            processSection(section, {
              ...parent_updates,
              element_id: new_element_id,
            })
          );
      };

      const processSection = (section, parent_updates) => {
        const new_section_id = tempId(section.id);

        processObject(section, "sections", {
          ...parent_updates,
          loaded: false,
        });

        const sectionColumnIdByPosition = {};
        Object.values(contextData.objects.variant.section_columns || {})
          .filter(
            (section_column) =>
              section_column.section_id === section.id &&
              !section_column.toBeDeleted
          )
          .forEach((section_column) => {
            const new_section_column_id = processObject(
              section_column,
              "section_columns",
              {
                section_id: new_section_id,
                loaded: false,
              }
            );
            sectionColumnIdByPosition[section_column.position] =
              new_section_column_id;
          });

        Object.values(contextData.objects.variant.elements)
          .filter(
            (element) =>
              element.section_id === section.id && !element.toBeDeleted
          )
          .forEach((element) => {
            const updates = { ...parent_updates, section_id: new_section_id };
            const new_section_column_id = element.section_column_id
              ? tempId(element.section_column_id)
              : sectionColumnIdByPosition[element.column];

            if (new_section_column_id) {
              updates.section_column_id = new_section_column_id;
            }

            processElement(element, updates, { keepPosition: true });
          });
      };

      const processStep = (step) => {
        const new_step_id = tempId(step.id);

        processObject(step, "steps", {
          position: step.position + 1,
          loaded: false,
        });

        const childSections = Object.values(
          contextData.objects.variant.sections
        ).filter(
          (section) =>
            section.step_id === step.id &&
            !section.element_id &&
            !section.content_toggle_item_id &&
            !section.toBeDeleted
        );

        childSections.forEach((section) =>
          processSection(section, { step_id: new_step_id })
        );

        const childElements = Object.values(
          contextData.objects.variant.elements
        ).filter(
          (element) => element.step_id === step.id && !element.toBeDeleted
        );

        childElements.forEach((element) =>
          processElement(element, { step_id: new_step_id })
        );
      };

      const processSectionColumn = (section_column) => {
        const new_section_column_id = tempId(section_column.id);
        const existing_positions = Object.values(
          contextData.objects.variant.section_columns || {}
        )
          .filter(
            (sc) =>
              sc.section_id == section_column.section_id && !sc.toBeDeleted
          )
          .map((sc) => parseInt(sc.position) || 0);
        const new_position =
          (existing_positions.length ? Math.max(...existing_positions) : 0) + 1;

        processObject(section_column, "section_columns", {
          position: new_position,
          loaded: false,
        });

        Object.values(contextData.objects.variant.elements || {})
          .filter(
            (element) =>
              element.section_id == section_column.section_id &&
              (element.section_column_id == section_column.id ||
                (element.section_column_id == null &&
                  element.column == section_column.position)) &&
              !element.toBeDeleted
          )
          .forEach((element) =>
            processElement(
              element,
              {
                section_id: section_column.section_id,
                column: new_position,
                section_column_id: new_section_column_id,
              },
              { keepPosition: true }
            )
          );
      };

      const objectProcessor = {
        steps: processStep,
        sections: (section) => processSection(section, {}),
        elements: (element) => processElement(element, {}),
        section_columns: processSectionColumn,
      };

      if (objectProcessor[object_type]) {
        objectProcessor[object_type](originalObject);
      }

      setContextData((originalData) => updatedBuilder);
    },

    scrollToObject: (type, id) => {
      let targetSelector = "";

      switch (type) {
        case "elements":
          targetSelector = `#element${id}`;
          break;
        case "sections":
          targetSelector = `#section${id}`;
          break;
        case "steps":
          targetSelector = `#step-${id}-canvas`;
          break;
        case "section_columns":
          targetSelector = `.cf-flex-column[data-section-column-id='${id}']`;
          break;
      }

      if (!targetSelector) return;

      const newObjectElement = document.querySelector(
        `#builder ${targetSelector}`
      );

      if (newObjectElement) {
        newObjectElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },

    scrollToObjectWithRetry: (type, id, maxAttempts = 12, delayMs = 120) => {
      const scrollAttempt = (attempt = 1) => {
        let targetSelector = "";

        switch (type) {
          case "elements":
            targetSelector = `#element${id}`;
            break;
          case "sections":
            targetSelector = `#section${id}`;
            break;
          case "steps":
            targetSelector = `#step-${id}-canvas`;
            break;
          case "section_columns":
            targetSelector = `.cf-flex-column[data-section-column-id='${id}']`;
            break;
          default:
            return;
        }

        const newObjectElement = document.querySelector(
          `#builder ${targetSelector}`
        );

        if (newObjectElement) {
          newObjectElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          return;
        }

        if (attempt < maxAttempts) {
          setTimeout(() => scrollAttempt(attempt + 1), delayMs);
        }
      };

      scrollAttempt();
    },

    addReplicaObjectToBuilder: (object_type, object_id, scrollTo = false) => {
      builder.manageReplicaObject(object_type, object_id, "add");

      if (scrollTo) {
        builder.scrollToObjectWithRetry(object_type, `temp-${object_id}`);
      }
    },

    removeReplicaObjectFromBuilder: (object_type, object_id) => {
      builder.manageReplicaObject(object_type, object_id, "remove");
    },

    addTemplatePlaceholderToBuilder: (object_type, parent) => {
      let updatedBuilder = { ...contextData };
      const tempId = `temp-placeholder-${_.uniqueId()}`;

      const base = {
        id: tempId,
        variant_id: updatedBuilder.objects.variant.id,
        loaded: false,
        placeholder: true,
        position: parent.position,
      };

      let placeholder;

      switch (object_type) {
        case "sections":
          placeholder = {
            ...base,
            step_id: parent.step_id,
            element_id: parent.element_id || null,
            options: { structure: {} },
            layout_type: "flex",
            section_columns: [],
          };
          break;
        case "steps":
          placeholder = {
            ...base,
            options: {},
          };
          break;
        case "elements":
          placeholder = {
            ...base,
            section_id: parent.section_id,
            column: parent.column,
            element_type: "html",
            html: "",
            options: {},
          };
          break;
        default:
          return null;
      }

      updatedBuilder.objects.variant[object_type][tempId] = placeholder;
      setContextData((originalData) => updatedBuilder);

      return tempId;
    },

    removeTemplatePlaceholderFromBuilder: (object_type, temp_id) => {
      let updatedBuilder = { ...contextData };
      delete updatedBuilder.objects.variant[object_type][temp_id];
      setContextData((originalData) => updatedBuilder);
    },

    // adds new object to context
    addObjectToBuilder: (
      object_type,
      object,
      skip_history,
      updates,
      forceRender
    ) => {
      let updatedBuilder = { ...contextData };
      updates = updates ? updates : [];
      object["toBeCreated"] = true;
      updatedBuilder.objects.variant[object_type][object.id] = object;

      if (skip_history !== true) {
        updates.push({
          object_type: object_type,
          object_id: object.id,
          setting_name: "[toBeDeleted]",
          value: undefined,
          old_value: true,
        });

        if (forceRender == true) {
          setContextData((originalData) => updatedBuilder);
          dispatchCustomEvent("updateBuilder", { updates: updates });
        } else {
          updatedBuilder = addChangeToHistory(updatedBuilder, {
            updates: updates,
            timestamp: Date.now(),
          });
        }
      }

      if (forceRender !== true) {
        setContextData((originalData) => updatedBuilder);
        builder.setUnsavedLoop();
      }
    },

    // saves builder state to database
    save: async function () {
      let button = document.getElementById("save");

      let updatedBuilder = { ...contextData };
      let hasObjectsToDelete =
        JSON.stringify(updatedBuilder).indexOf('"toBeDeleted":true') > -1;
      let saveSuccessful = true;
      let hasDeleteFailures = false;

      if (
        hasObjectsToDelete == false ||
        confirm(
          "Before saving, are you sure you want to permanently delete the elements you've removed?\r\n\r\nWarning: Your undo history will be cleared upon saving."
        )
      ) {
        button.classList.add("disabled");
        button.innerHTML =
          '<i class="glyphicon glyphicon-repeat spin-animation" style="font-size: 12px !important; margin: 0px; font-weight: bold;"></i>';
        button.classList.add("saving");

        let updateRequests = [];
        let deleteRequests = [];
        let objectsSuccessfullyUpdated = [];
        let objectsToBeDeleted = [];

        let object_types = [
          "variant",
          "step",
          "section",
          "section_column",
          "element",
          "field",
          "field_option",
          "content_list_item",
          "product",
          "product_variant",
          "content_toggle_item",
          "review_recommendation",
          "scheduler_recommendation",
          "embed_recommendation",
          "product_recommendation",
          "action_group",
          "condition",
          "action",
        ];

        object_types.forEach((object_type) => {
          let api_root;
          let objectsToBeUpdated;

          // find objects that need to be updated
          if (object_type == "variant") {
            api_root = "./";
            let variant = JSON.parse(
              JSON.stringify(updatedBuilder.objects["variant"])
            );
            let object_types_extended = [...object_types, "action"];
            object_types_extended.forEach((object_type) => {
              delete variant[object_type + "s"];
            });
            objectsToBeUpdated = [];
            variant.options["unpublished"] = "true";
            objectsToBeUpdated.push(variant);
          } else {
            api_root = "./" + object_type + "s/";
            objectsToBeUpdated = Object.values(
              updatedBuilder.objects["variant"][object_type + "s"]
            ).filter(
              (object) =>
                object.toBeUpdated == true || object.toBeCreated == true
            );
          }

          // add PUT calls to save request
          objectsToBeUpdated.forEach((object) => {
            let api_endpoint =
              object_type == "variant" ? api_root : api_root + object.id;

            let payload = {};
            payload[object_type] = object;

            if (object_type == "action") {
              payload = { action_object: payload.action };
            }

            updateRequests.push(apiRequest("PUT", api_endpoint, payload));
            objectsSuccessfullyUpdated.push(object);
          });

          // add DELETE calls to save request
          if (object_type !== "variant") {
            const objectsMarkedForDeletion = Object.values(
              updatedBuilder.objects["variant"][object_type + "s"]
            ).filter((object) => object.toBeDeleted == true);

            objectsMarkedForDeletion.forEach((object) => {
              objectsToBeDeleted.push({
                object_type: object_type,
                object_id: object.id,
                delete_path: api_root + object.id,
              });
            });
          }
        });

        if (updateRequests.length > 0) {
          const updateResults = await Promise.all(updateRequests);

          if (
            updateResults.filter((result) =>
              [408, 401, 422, 500, 503].includes(result.code)
            ).length == 0
          ) {
            // register successful save with builder context
            updatedBuilder.history.unsaved = false;
            updatedBuilder.history.unpublished = true;

            objectsSuccessfullyUpdated.forEach((object) => {
              delete object["toBeUpdated"];
              delete object["toBeCreated"];
            });

            // process delete requests after updates succeeded
            if (objectsToBeDeleted.length > 0) {
              const DELETE_TIMEOUT_MS = 10000;
              const timeoutResult = {
                code: 408,
                message: "Request timed out",
                json: {},
              };

              const deleteRequests = objectsToBeDeleted.map(
                (objectToBeDeleted) => {
                  const timeout = new Promise((resolve) =>
                    setTimeout(() => resolve(timeoutResult), DELETE_TIMEOUT_MS)
                  );

                  return Promise.race([
                    apiRequest("DELETE", objectToBeDeleted.delete_path),
                    timeout,
                  ]).then((result) => ({
                    ...result,
                    objectToBeDeleted,
                  }));
                }
              );

              const deleteResults = await Promise.all(deleteRequests);
              const failedDeletes = deleteResults.filter((r) =>
                [408, 401, 500, 503].includes(r.code)
              );

              deleteResults.forEach(({ code, objectToBeDeleted }) => {
                if (![408, 401, 500, 503].includes(code)) {
                  delete updatedBuilder.objects["variant"][
                    objectToBeDeleted.object_type + "s"
                  ][objectToBeDeleted.object_id];
                }
              });

              if (failedDeletes.length > 0) {
                hasDeleteFailures = true;
                updatedBuilder.history.unsaved = true;
                alert(
                  `Save incomplete: ${failedDeletes.length} item(s) could not be deleted. Please try saving again.`
                );
              }

              updatedBuilder.history.changes = [];
              updatedBuilder.history.undos = 0;

              const selectedObjectType = updatedBuilder["selected_object_type"];
              const selectedObjectId = updatedBuilder["selected_object_id"];
              const selectedObject =
                selectedObjectType &&
                selectedObjectId &&
                updatedBuilder.objects["variant"][selectedObjectType]
                  ? updatedBuilder.objects["variant"][selectedObjectType][
                      selectedObjectId
                    ]
                  : null;

              if (
                !selectedObject ||
                selectedObject.toBeDeleted == true ||
                (selectedObjectType == "sections" && selectedObject.element_id)
              ) {
                updatedBuilder["selected_object_type"] = null;
                updatedBuilder["selected_object_id"] = null;
              }
            }

            setContextData((originalData) => updatedBuilder);
          } else {
            // alert user about unsuccessful save
            saveSuccessful = false;
            button.innerText = "Save";
            button.classList.remove("disabled");
            if (
              updateResults.filter((result) => [401].includes(result.code))
                .length > 0
            ) {
              alert(
                "Save unsuccessful because you are logged out, or you do not have the correct permissions. \r\n\r\nWARNING: To preserve your work, please log back in from another tab and then try saving here again."
              );
            } else if (
              updateResults.filter((result) =>
                [408, 422, 500, 503].includes(result.code)
              ).length > 0
            ) {
              alert("Save unsuccessful. Please try again!");
            }
          }
          builder.clearUnsavedLoop();
          if (hasDeleteFailures) {
            builder.setUnsavedLoop();
          }
        }
      }

      button.innerText = "Save";
      return saveSuccessful;
    },

    publish: async function () {
      let button = document.getElementById("publish");
      let updatedBuilder = { ...contextData };
      let publishSuccessful = true;

      if (
        !updatedBuilder.objects.variant.options.unpublished ||
        updatedBuilder.history.unpublished == true
      ) {
        if (button) {
          button.innerHTML =
            '<i class="glyphicon glyphicon-repeat spin-animation" style="font-size: 12px !important; margin: 0px; font-weight: bold;"></i>';
        }

        let variant = JSON.parse(
          JSON.stringify(updatedBuilder.objects.variant)
        );

        [
          "variants",
          "steps",
          "sections",
          "elements",
          "fields",
          "field_options",
          "products",
          "product_variants",
          "product_recommendations",
          "review_recommendations",
          "scheduler_recommendations",
          "embed_recommendations",
          "action_groups",
          "conditions",
          "actions",
        ].forEach((object_type) => {
          delete variant[object_type];
        });

        const result = await apiRequest("PUT", "./", {
          variant: variant,
          publish: true,
        });

        if (result && result.code == 200) {
          // register successful publish to builder context
          updatedBuilder.history.unsaved = false;
          updatedBuilder.history.unpublished = false;
          setContextData((originalData) => updatedBuilder);
        } else {
          // alert use about unsuccessful publish
          publishSuccessful = false;
          if (button) {
            button.innerText = "Publish";
            button.classList.remove("disabled");
          }
          if (result && result.code == 401) {
            alert(
              "Publish unsuccessful because you are logged out, or you do not have the correct permissions. Please log back in from another tab and then try publishing here again."
            );
          } else {
            alert("Publish unsuccessful. Please try again.");
          }
        }
      }

      if (button) {
        button.innerText = "Publish";
      }
      return publishSuccessful;
    },

    listBackups: async function () {
      const result = await apiRequest("GET", "./backups");

      if (result && result.code == 200) {
        return { success: true, backups: result.json || [] };
      }

      return {
        success: false,
        error:
          (result && result.json && result.json.error) ||
          "Unable to load past versions.",
      };
    },

    restoreBackup: async function (backupId) {
      const result = await apiRequest("POST", "./restore", {
        backup_id: backupId,
      });

      if (result && result.code == 200 && result.json.success == true) {
        return { success: true, response: result.json };
      }

      return {
        success: false,
        error: "Something went wrong",
      };
    },

    // alerts the user upon leaving about unsaved changes
    unsavedWarning: () => {
      let message =
        contextData.history.unsaved == true
          ? "You have unsaved changes, are you sure want to leave?"
          : "";

      let links = Array.from(document.querySelectorAll("a")).filter(
        (a) =>
          a.href &&
          a.href !== "#" &&
          a.classList.contains("builder-link") == false
      );

      links.forEach((a) => {
        a.onclick = function (e) {
          if (
            ["button", "role"].includes(a.getAttribute("role")) == false &&
            a.classList.contains("fr-command") == false &&
            a.classList.contains("shepherd-button") == false &&
            a.classList.contains("shepherd-cancel-link") == false &&
            a.classList.contains("cf-logo-link") == false &&
            $(a).parents(".fr-inline").length == 0 &&
            $(a).parents(".bootstrap-datetimepicker-widget").length == 0
          ) {
            if (
              window.leaving !== true &&
              contextData.history.unsaved == true &&
              confirm(message)
            ) {
              e.preventDefault();
              window.leaving = true;

              if (contextData.history.unpublished !== true) {
                builder.deleteObjectsToBeCreated();
              }

              setTimeout(function () {
                location.href = a.href;
              }, 500);
            }
          }
        };
      });

      function unloadAlert(e) {
        if (
          window.leaving !== true &&
          (contextData.history.unsaved == true ||
            contextData.history.unpublished == true)
        ) {
          e = e || window.event;
          if (e) {
            e.returnValue = message;
          }
          return message;
        }
      }

      if (environment !== "test") {
        window.addEventListener("beforeunload", unloadAlert);
      }
    },

    setUnsavedLoop: () => {
      let unsavedLoopInterval = () => {
        if (
          contextData.history.unsaved == true &&
          window.selected_object_editing !== true &&
          window.filestackOpen !== true &&
          confirm(
            "You haven't saved your changes in a while. Want to save now?"
          )
        ) {
          builder.save();
          builder.clearUnsavedLoop();
        } else {
          builder.clearUnsavedLoop();
          builder.setUnsavedLoop();
        }
      };

      if (typeof window.unsavedLoop == "undefined") {
        window.unsavedLoop = setInterval(unsavedLoopInterval, 600000);
      }
    },

    clearUnsavedLoop: () => {
      clearInterval(window.unsavedLoop);
      window.unsavedLoop = undefined;
    },

    // deletes unsaved created objects upon intentional navigation
    deleteObjectsToBeCreated: async () => {
      let object_types = [
        "steps",
        "sections",
        "elements",
        "fields",
        "field_options",
        "content_list_items",
        "products",
        "content_toggle_items",
        "product_variants",
        "product_recommendations",
        "review_recommendations",
        "scheduler_recommendations",
        "embed_recommendations",
        "actions",
        "conditions",
        "action_groups",
      ];

      const CLEANUP_TIMEOUT_MS = 5000;
      const deleteRequests = [];

      object_types.forEach((object_type) => {
        let objectsToBeDeleted = Object.values(
          contextData.objects["variant"][object_type]
        ).filter((object) => object.toBeCreated == true && object.id);

        objectsToBeDeleted.forEach((object) => {
          const timeout = new Promise((resolve) =>
            setTimeout(resolve, CLEANUP_TIMEOUT_MS)
          );

          deleteRequests.push(
            Promise.race([
              apiRequest("DELETE", "./" + object_type + "/" + object.id),
              timeout,
            ])
          );
        });
      });

      await Promise.allSettled(deleteRequests);
    },

    // shows a feature gate / upgrade prompt depending on feature type, subscription plan and website premium status
    displayFeatureGate: (event, feature_gate_type, value) => {
      const is_premium_website = contextData.objects.website.premium == true;
      const plan_type =
        window.plan_type || contextData.objects.subscription.plan_type;
      const is_super_admin =
        contextData.objects.current_user.super_admin == true;
      var modal = $("#upgrade");
      var after_2024_deprecated =
        $(modal).attr("data-account-after-2024-deprecation") == "true";

      let label;
      let required_plan;
      let should_display = false;

      if (
        feature_gate_type == "Conditional Action" ||
        feature_gate_type == "Skip Logic" ||
        feature_gate_type == "Conditional Jump"
      ) {
        label = "use conditional funnel logic";
        required_plan = "pro";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2024_pages",
            "2024_core",
            "2024_funnels",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Conditional Product Recommendations") {
        label = "add conditional product recommendations";
        required_plan = "pro";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2024_pages",
            "2024_core",
          ].includes(plan_type) ||
          (plan_type == "2024_funnels" && after_2024_deprecated) ||
          is_premium_website == false;
      }

      if (feature_gate_type == "Conditional Review Recommendations") {
        label = "add conditional review recommendations";
        required_plan = plan_type.indexOf("2026") > -1 ? "pro" : "plus";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2025_pro",
            "2024_pro",
            "2024_pages",
            "2024_funnels",
            "2024_core",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Conditional Scheduler Recommendations") {
        label = "add conditional scheduler recommendations";
        required_plan = plan_type.indexOf("2026") > -1 ? "pro" : "plus";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2025_pro",
            "2024_pro",
            "2024_pages",
            "2024_funnels",
            "2024_core",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Conditional Embed Recommendations") {
        label = "add conditional embed recommendations";
        required_plan = plan_type.indexOf("2026") > -1 ? "pro" : "plus";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2025_pro",
            "2024_pro",
            "2024_pages",
            "2024_funnels",
            "2024_core",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Custom HTML") {
        label = "add custom HTML";
        required_plan = "pro";
        should_display =
          (plan_type == "free" || is_premium_website == false) &&
          !is_super_admin;
      }

      if (feature_gate_type == "Custom Scripts") {
        label = "add conversion pixels, custom javascript & CSS";
        required_plan = "pro";
        should_display =
          (plan_type == "free" || is_premium_website == false) &&
          !is_super_admin;
      }

      if (feature_gate_type == "Funnel Score") {
        label = "add funnel scores";
        required_plan = "paid";
        should_display =
          (plan_type == "free" || is_premium_website == false) &&
          !is_super_admin;
      }

      if (feature_gate_type == "Funnel Score CRM") {
        label = "save funnel scores to your CRM";
        required_plan = "prime";
        should_display =
          ([
            "free",
            "2026_personal",
            "2024_pages",
            "2024_core",
          ].includes(plan_type) ||
            is_premium_website == false) &&
          !is_super_admin;
      }

      if (feature_gate_type == "Email Validation") {
        label = "enable real-time email & phone verification form fields";
        required_plan = "pro";
        should_display =
          [
            "free",
            "2026_personal",
            "2025_pro",
            "2024_pages",
            "2024_core",
            "2024_funnels",
            "2024_pro",
            "marketer",
            "2021_pro",
            "2022_pro",
            "2023_build",
            "2023_launch",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Phone Validation") {
        label = "enable real-time email & phone verification form fields";
        required_plan = "pro";
        should_display =
          [
            "free",
            "2026_personal",
            "2025_pro",
            "2024_pages",
            "2024_core",
            "2024_funnels",
            "2024_pro",
            "2023_build",
            "2023_launch",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (feature_gate_type == "Upsells & Cross-sells" && value == "related") {
        label = "launch targeted upsells & cross-sells";
        required_plan = plan_type.indexOf("2026") > -1 ? "pro" : "plus";
        should_display =
          [
            "free",
            "2026_prime",
            "2026_personal",
            "2025_pro",
            "2024_pages",
            "2024_core",
          ].includes(plan_type) ||
          (plan_type == "2024_pro" && after_2024_deprecated) ||
          is_premium_website == false;
      }

      if (
        feature_gate_type == "Upsells & Cross-sells" &&
        ["filter_quiz_answers", "filter"].includes(value)
      ) {
        label = "create conditional product recommendations";
        required_plan = "pro";
        should_display =
          [
            "free",
            "2026_personal",
            "2026_prime",
            "2024_pages",
            "2024_core",
          ].includes(plan_type) || is_premium_website == false;
      }

      if (should_display) {
        subscriptions.upgrade_modal(
          event,
          false,
          label,
          required_plan,
          "Triggered feature CTA – " + feature_gate_type
        );
      }

      return !should_display;
    },

    helpers: {
      convertLegacyPadding: function (oldValue) {
        const levelsMap = {
          0: 0,
          1: 15,
          2: 25,
          3: 40,
          4: 70,
          5: 120,
        };

        let newValue;
        if (oldValue) {
          if (typeof oldValue == "string" && oldValue.indexOf("_") > -1) {
            let level = oldValue.split("_").pop();
            newValue = levelsMap[level];
          } else {
            newValue = oldValue;
          }
        }
        return newValue;
      },
    },

    toggleDarkMode: () => {
      contextData.dark_mode = !contextData.dark_mode;
      setCookie("darkMode", `${contextData.dark_mode}`);
      document.body.setAttribute(
        "data-mode",
        contextData.dark_mode ? "dark" : "light"
      );

      let updatedBuilder = { ...contextData };
      updatedBuilder.dark_mode = contextData.dark_mode;
      setContextData((originalData) => updatedBuilder);
    },

    toggleZoomableCanvas: () => {
      contextData.zoomable_canvas_locked = !contextData.zoomable_canvas_locked;
      setCookie(
        "zoomableCanvasLocked",
        `${contextData.zoomable_canvas_locked}`
      );
      let updatedBuilder = { ...contextData };
      updatedBuilder.zoomable_canvas_locked =
        contextData.zoomable_canvas_locked;
      setContextData((originalData) => updatedBuilder);
    },

    view: (view, collapse) => {
      let updatedBuilder = { ...contextData };
      updatedBuilder.view = view;
      updatedBuilder.selected_object_type = null;
      updatedBuilder.selected_object_id = null;
      updatedBuilder.selected_object_tab = null;
      updatedBuilder.selected_object_collapse = collapse ? collapse : null;
      updatedBuilder.selected_item_parent_id = null;
      updatedBuilder.selected_item_id = null;
      updatedBuilder.selected_item_type = null;
      updatedBuilder.panel_closed = view == null;
      setContextData((originalData) => updatedBuilder);

      window.selected_object_type = null;
      window.selected_object_id = null;
      window.selected_object_tab = null;
      window.selected_object_collapse = collapse ? collapse : null;
      window.selected_object_editing = null;
      window.panel_closed = view == null;
    },

    focus: (parent_id, item_type, item_id, triggerPanelVisibility = true) => {
      let updatedBuilder = { ...contextData };
      updatedBuilder.selected_item_parent_id = parent_id;
      updatedBuilder.selected_item_id = item_id;
      updatedBuilder.selected_item_type = item_type;
      updatedBuilder.focused_item_id = item_id;
      updatedBuilder.focused_item_type = item_type;
      updatedBuilder.trigger_panel_visibility = triggerPanelVisibility;
      setContextData((originalData) => updatedBuilder);
    },
  };

  // updates builder history with given change
  function addChangeToHistory(updatedBuilder, change) {
    // add to updates if no undos in state
    if (updatedBuilder.history.undos == 0) {
      updatedBuilder.history.changes.unshift(change);
    }

    // if undos in state, replace undone changes with new changes, keeping undo history
    if (updatedBuilder.history.undos > 0) {
      let new_changes = [];

      updatedBuilder.history.changes.forEach((change, i) => {
        if (i >= updatedBuilder.history.undos) {
          new_changes.unshift(change);
        }
      });

      new_changes.unshift(change);
      updatedBuilder.history.changes = new_changes;
    }

    updatedBuilder.history.undos = 0;
    updatedBuilder.history.unsaved = true;
    return updatedBuilder;
  }

  return (
    <VariantContextData.Provider value={[contextData, builder, setContextData]}>
      <React.Fragment>
        <div
          className={`row hard-center variant-edit ${
            contextData.dark_mode ? "dark" : ""
          }`}
        >
          {props.children}
        </div>
      </React.Fragment>
    </VariantContextData.Provider>
  );
};
